import { AxiosRequestConfig } from 'axios';
import flatten from 'lodash-es/flatten';

import { FORM_DATA_HEADER, URLS } from '@/constants';
import { PaginatedResponse } from '@/models/api';
import { OrderSummaryComment } from '@/models/customer';
import { LogisticsFirstOrder, LogisticsOrder } from '@/models/logistics';
import {
  AuditLog,
  CloneOrderResponse,
  CollabOrderSeller,
  CommerceOrder,
  CommerceOrderById,
  CommerceOrderSentById,
  Order,
  OrderDocument,
  OrderDocumentDelete,
  OrderDocumentPayload,
  OrderFromSplitSummary,
  OrderLineItemSellThroughHistoryAdjustmentDetail,
  OrderPayment,
  OrderPromoCodeDelete,
  OrderReceived,
  OrderReturn,
  OrderSellThroughHistory,
  OrderSent,
  OrderSummaryOrder,
  OrderSummaryOrderInformation,
} from '@/models/order';
import { OrderAttachedPackage } from '@/models/traceability';
import { useUserStore } from '@/piniaStores/user/user.store';
import session, { apiSession, apiSlugSession, createSession, slugSession } from '@/services/session';
import OrderReturnStore from '@/store/orders/OrderReturn';
import { buildPath } from '@/utils';

import { OrderReturnData, OrderTransitionStatusPayload } from './models/order';

interface GetOrdersRequestParams {
  uuid: string;
  isBuyer?: boolean;
}

interface GetOrdersParams extends Omit<GetOrdersRequestParams, 'uuid'> {
  [k: string]: unknown;
  uuid?: string;
}

// serialize filters to a string of params
// e.g. status: ['fulfilled', 'complete'] => 'status=fulfilled&status=complete'
// The orders types need a lot of help
const mapSerializedParams = (userParams: Record<string, unknown>) =>
  Object.keys(userParams).reduce((acc: unknown[], key) => {
    if (Array.isArray(userParams[key])) {
      acc.push((userParams[key] as string[]).map((value) => `${key}=${value}`));

      delete userParams[key];
    }

    return acc;
  }, []);

/**
 * type guard to differentiate the response type:
 *  - Paginated or Single item
 * @returns boolean
 */
const isPaginatedResponse = (response): response is PaginatedResponse => Array.isArray(response?.data?.results);

/**
 * Get orders. A single order may be retrieved by providing a `uuid`
 * TODO: rename to `getOrdersReceived`
 * @param {object} [data] Object with params to pass
 * @param {string} [data.uuid] uuid is passed when fetching only one order
 * @param {boolean} [data.isBuyer] boolean to fetch buyer orders
 * @param {object} [data.params] Any query additional API query parameters
 * @return {object|array} TODO -- make this consistent.
 */
type GetOrdersReturnType<T> =
  // Paginated response when calling `commerce/orders-sent/{uuid}`
  T extends { uuid: string; isBuyer: true }
    ? CommerceOrderSentById
    : // Paginated response when calling `commerce/orders/{uuid}`
    T extends { uuid: string }
    ? CommerceOrderById
    : // Paginated response when calling `commerce/orders-sent`
    T extends { isBuyer: true }
    ? OrderSent[]
    : // Paginated response when calling `commerce/orders`
      CommerceOrder[];

export async function getOrders({ uuid = '', isBuyer = false, ...userParams }: GetOrdersParams = {}): Promise<
  GetOrdersReturnType<GetOrdersParams>
> {
  const serializedParams = mapSerializedParams(userParams);

  const url = isBuyer ? URLS.ORDERS.SENT : URLS.ORDERS.BASE;
  const singleParams = isBuyer ? {} : { include_children: 'line_items,credits_applied' };

  const stringifiedParams = serializedParams.length ? `?${flatten(serializedParams).join('&')}` : '';
  const orderId = uuid ? `${uuid}/` : stringifiedParams;

  const params = {
    ...(uuid ? singleParams : { limit: 999 }),
    ...userParams,
  };

  const response = await session.get(`${url}/${orderId}`, { params });

  return isPaginatedResponse(response) ? response.data.results : response.data;
}

export async function getCommerceOrders(userParams: Record<string, unknown>) {
  const url = URLS.ORDERS.BASE;

  const serializedParams = mapSerializedParams(userParams);
  const stringifiedParams = serializedParams.length ? `?${flatten(serializedParams).join('&')}` : '';

  const params = {
    limit: 50,
    offset: 0,
    ...userParams,
  };

  const response = await session.get<PaginatedResponse<CommerceOrder>>(`${url}/${stringifiedParams}`, { params });

  return response.data;
}

/**
 * Get list of order's attached packages
 * @param {string} [uuid] uuid of order
 */
export async function getOrderAttachedPackages(uuid: string) {
  const { ORDERS, CUSTOM_ERROR } = URLS;
  const url = `${ORDERS.BASE}/${uuid}/${ORDERS.PACKAGES}/?${CUSTOM_ERROR}`;
  const response = await session.get<OrderAttachedPackage[]>(url);

  return response.data;
}

/**
 * Get orders received.
 * @param {object} params  GET params for orders received list
 */
export async function getOrdersReceived(params: Record<string, unknown> = {}) {
  const response = await session.get<PaginatedResponse<OrderReceived>>(URLS.ORDERS.BASE, {
    params: {
      status__not: 'draft',
      error_format: 'jsonapi',
      ...params,
    },
  });

  return response.data;
}

/**
 * Get orders received.
 * @param {string} params Serialized GET params from ServerListItem (or "from api-driven LLListView")
 */
export async function getOrdersReceivedListView(params: string) {
  const url = `${URLS.ORDERS.BASE}/?status__not=draft&${URLS.CUSTOM_ERROR}&${params}`;
  const response = await session.get<PaginatedResponse<OrderReceived>>(url);

  return response.data;
}

/**
 * Get buyer orders sent.
 * @param {string} params GET params
 * @param {boolean} allLicenses when true, appends param to URL to get orders from all licenses
 */
export async function getOrdersSent({
  params,
  allLicenses,
}: { params?: Record<string, string | string[] | boolean>; allLicenses?: boolean } = {}) {
  const response = await createSession(URLS.INTERNAL).get<PaginatedResponse<OrderSent>>(URLS.ORDERS.SENT, {
    params: {
      status__not: 'draft',
      error_format: 'jsonapi',
      ...params,
      company_groups: allLicenses ? true : undefined,
    },
  });

  return response.data;
}

/**
 * Get Collaborative Orders Received (Buyer)
 * @param params - Serialized GET params
 */
export async function getCollabOrdersReceived(options: { params?: string; allLicenses?: boolean } = {}) {
  const newParams = options.allLicenses ? `${options.params}&company_groups=true` : options.params;
  const url = `${URLS.ORDERS.SENT}/?status=draft&collaborative=true&${URLS.CUSTOM_ERROR}&${newParams}`;
  const response = await createSession(URLS.INTERNAL).get<PaginatedResponse<OrderSent>>(url);

  return response.data;
}

/**
 * Get Recent Collaborative Orders Received (Buyer) for the dashboard widget
 */
export async function getRecentCollabOrdersReceived() {
  const url = `${URLS.ORDERS.SENT}/?status=draft&collaborative=true&limit=5&offset=0&sort_by=-modified&${URLS.CUSTOM_ERROR}`;
  const response = await createSession(URLS.INTERNAL).get<PaginatedResponse<OrderSent>>(url);

  return response.data.results;
}

/**
 * Get Collaborative Orders sent (seller)
 * @param params - Serialized GET params
 */
export async function getCollaborativeOrders(params: string) {
  const newParams = `${params}&status=draft&collaborative=true`;
  const url = `${URLS.ORDERS.BASE}/?${URLS.CUSTOM_ERROR}&${newParams}`;
  const response = await session.get<PaginatedResponse<CollabOrderSeller>>(url);

  return response.data;
}

/**
 * Re-add items to the cart from the given order
 * @param {string} args.uuid - an order number
 */
export function reorderBGO(args: { uuid: string }) {
  const { uuid } = args || {};

  return createSession(URLS.INTERNAL).post(`orders/${uuid}/${URLS.ORDERS.REORDER}/?${URLS.CUSTOM_ERROR}`);
}

export interface CloneOrderArgs {
  orderNumber: string;
  customerId?: number;
}

/**
 * Clones an order and returns the new order number (uuid) and original short order id.
 * Optionally accepts a customerId to assign the new order to a different customer.
 */
export async function cloneOrder({ orderNumber, customerId }: CloneOrderArgs): Promise<CloneOrderResponse> {
  const url = `${URLS.ORDERS.BASE}/${orderNumber}/${URLS.ORDERS.CLONE}/`;
  const body = { customer_id: customerId };
  const params: AxiosRequestConfig['params'] = { error_format: 'jsonapi' };

  const response = await apiSession.post<CloneOrderResponse>(url, body, { params });

  return response.data;
}

/**
 * Get order logs.
 * @param {string} data.uuid of the order
 * @param {Object} data.params GET parameters for Order/Audit Endpoints.
 * @return Audit Order Log data
 */
export async function getOrderLogs(args: { limit: number; nextCursor: string; uuid: string }) {
  const { uuid, nextCursor } = args;
  const url = `${URLS.ORDERS.LOGS}/${uuid}?${URLS.CUSTOM_ERROR}`;
  const params = {
    limit: 25,
    next_cursor: nextCursor,
  };

  const response = await session.get<AuditLog>(url, { params });

  return response.data;
}

/**
 * Get BME orders.
 * @param {string} params Serialized GET params from ServerListItem (or "from api-driven LLListView")
 */
export async function getBMEOrders(params: string) {
  const url = `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.ORDERS}/?${URLS.CUSTOM_ERROR}&${params}&status__not=draft`;
  const response = await apiSlugSession.get<PaginatedResponse>(url);

  return response.data;
}

/**
 * Get BME draft orders.
 * @param {string} params Serialized GET params from ServerListItem (or "from api-driven LLListView")
 */
export async function getBMEDraftOrders(params: string) {
  const { BASE, ORDERS } = URLS.BRAND_OWNER;
  const url = `${BASE}/${ORDERS}/?${URLS.CUSTOM_ERROR}&${params}&status=draft`;
  const response = await apiSlugSession.get<PaginatedResponse>(url);

  return response.data;
}

/**
 * Get a single BME order by providing a uuid
 * @param {string} uuid of the order
 */
export async function getBMEOrder(uuid: string) {
  const params = {
    include_children: 'line_items',
  };

  const response = await apiSlugSession.get(`${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.ORDERS}/${uuid}/`, {
    params,
  });

  return response.data;
}

/**
 * Get line items for an order
 * @param {string} uuid number of the order
 * @return an array of line items
 */
export async function getOrderLineItems(uuid: string) {
  const response = await session.get(`${URLS.ORDERS.BASE}/${uuid}/${URLS.ORDERS.LINE_ITEMS}/?${URLS.CUSTOM_ERROR}`);

  return response.data;
}

/**
 * Get line items for a BME order
 * @param {string} uuid number of the order
 * @return an array of line items
 */
export async function getBMEOrderLineItems(uuid: string) {
  const url = `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.LINE_ITEMS}/?${URLS.CUSTOM_ERROR}&order=${uuid}`;

  const response = await apiSlugSession.get<PaginatedResponse>(url);

  return response.data;
}

/**
 * Creates an Order. By default, the Order status will be "Draft".
 * @param {object} data The data needed to create an order, it's including optional isBrandOwner param for BME Orders
 */
export const createOrder = (order: Order) => {
  const defaults = {
    // TODO pull out of here; perhaps add TS interface
    discount_amount: 0.0,
    line_items: [],
    status: 'draft',
  };
  const payload: Order = { ...defaults, ...order };

  if (order.isBrandOwner) {
    return apiSlugSession
      .post(`${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.ORDERS}/?${URLS.CUSTOM_ERROR}`, payload)
      .then((response) => response.data);
  }

  return session.post(`${URLS.ORDERS.BASE}/?${URLS.CUSTOM_ERROR}`, payload).then((response) => response.data);
};

/**
 * Updates a collaborative order by Buyer.
 * @param data The partial data to update an Order
 */
export const updateCollabOrdersReceived = (data) => {
  return session
    .patch<Order>(`${URLS.ORDERS.SENT}/${data.uuid}/?include_children=line_items&${URLS.CUSTOM_ERROR}`, data)
    .then((response) => response.data);
};

/**
 * Updates an existing order.
 * @param data The partial data to update an Order
 */
export const updateOrder = (data) => {
  return session
    .patch<Order>(`${URLS.ORDERS.BASE}/${data.uuid}/?include_children=line_items&${URLS.CUSTOM_ERROR}`, data)
    .then((response) => response.data);
};

/**
 * Updates a BME existing order.
 * @param data The partial data to update an Order
 */
export const updateBMEOrder = (data) => {
  return apiSlugSession
    .patch(
      `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.ORDERS}/${data.uuid}/?include_children=line_items&${URLS.CUSTOM_ERROR}`,
      data,
    )
    .then((response) => response.data);
};

/**
 * Transitions a collabrative order status by Buyer
 * @param {string} data.uuid of the order
 * @param {string} data.state to which the order will be transitioned
 */
// eslint-disable-next-line require-await
export async function transitionCollabOrdersReceived(data: { uuid: string; state: string }) {
  const { uuid, state, ...params } = data;

  return session.post(`${URLS.ORDERS.SENT}/${uuid}/${URLS.ORDERS.TRANSITION}/${state}/?${URLS.CUSTOM_ERROR}`, params);
}

/**
 * Transitions an order's status
 * @param {string} [data.uuid] of the order
 * @param {string} [data.state] to which the order will be transitioned
 */
// eslint-disable-next-line require-await
export async function transitionOrder(data: OrderTransitionStatusPayload) {
  const { uuid, state, ...params } = data;
  // FIXME: internal/commerce/orders does not work for order transitioning

  return session.post(`${URLS.ORDERS.RECEIVED}/${uuid}/transition/${state}/?${URLS.CUSTOM_ERROR}`, params);
}

/**
 * Combines multiple orders
 * @param {string[]} [data.uuids] of the orders to be combined
 */
// eslint-disable-next-line require-await
export async function combineOrders(data: { uuids: string[] }) {
  // FIXME: internal/commerce/orders does not work for combining orders

  return session.post(`${URLS.ORDERS.RECEIVED}/${URLS.ORDERS.COMBINE}/?${URLS.CUSTOM_ERROR}`, data);
}

/**
 * Deletes an order
 * @param {string} uuid of the order to delete
 */
// eslint-disable-next-line require-await
export async function removeOrder(uuid: string) {
  return session.delete(`${URLS.ORDERS.BASE}/${uuid}/?${URLS.CUSTOM_ERROR}`);
}

/**
 * Deletes a BME order
 * @param {string} uuid of the order to delete
 */
// eslint-disable-next-line require-await
export async function removeBMEOrder(uuid: string) {
  return apiSlugSession.delete(`${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.ORDERS}/${uuid}/?${URLS.CUSTOM_ERROR}`);
}

/**
 * Get order taxes for orders sold by companies the user has access to
 * If an order uuid is provided, it will return the taxes from that order
 * @param {data} data data needed to get order taxes - uuid, and optional isBrandOwner
 */
export const getOrderTaxes = (data) => {
  let params = {};
  const { uuid, isBrandOwner } = data;
  const endpoint = isBrandOwner ? `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.ORDER_TAXES}` : URLS.ORDERS.TAXES;

  if (uuid) {
    params = {
      order: uuid,
    };
  }

  return (isBrandOwner ? apiSlugSession : session)
    .get(`${endpoint}/`, { params })
    .then((response) => response.data.results);
};

/**
 * Updates a tax from the order by changing its properties { is_applied: boolean, rate: number }
 * @param {Object} data - data needed to update tax:
 * @param {Object} data.tax - tax object
 * @param {boolean} [data.isBrandOwner] flag to indicate is BME (optional)
 */
export const updateTax = (data) => {
  const { isBrandOwner, tax } = data;
  const endpoint = isBrandOwner ? `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.ORDER_TAXES}` : URLS.ORDERS.TAXES;

  return (isBrandOwner ? apiSlugSession : session)
    .patch(`${endpoint}/${tax.id}/?${URLS.CUSTOM_ERROR}`, tax)
    .then((response) => response.data);
};

/**
 * Adds a tax to an order
 * @param {Object} data - data needed to add tax:
 * @param {Object} data.tax - tax object
 * @param {boolean} [data.isBrandOwner] flag to indicate is BME (optional)
 */
export const addTax = (data) => {
  const { isBrandOwner, tax } = data;
  const endpoint = isBrandOwner ? `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.ORDER_TAXES}` : URLS.ORDERS.TAXES;

  return (isBrandOwner ? apiSlugSession : session)
    .post(`${endpoint}/?${URLS.CUSTOM_ERROR}`, tax)
    .then((response) => response.data);
};

/**
 * Removes a tax from an order
 * @param {Object} data - data needed to remove tax:
 * @param {Object} data.tax - tax object
 * @param {boolean} [data.isBrandOwner] flag to indicate is BME (optional)
 */
export const removeTax = (data) => {
  const { isBrandOwner, tax } = data;
  const endpoint = isBrandOwner ? `${URLS.BRAND_OWNER.BASE}/${URLS.BRAND_OWNER.ORDER_TAXES}` : URLS.ORDERS.TAXES;

  return (isBrandOwner ? apiSlugSession : session)
    .delete(`${endpoint}/${tax.id}/?${URLS.CUSTOM_ERROR}`)
    .then((response) => response.data);
};

/**
 * Gets an array of one or many orderReturns
 * @param {object} [data] Object with query params
 * @param {string} [data.uuid] uuid is passed when loading only one return
 * @param {string[]} [data.expand] list of fields to get expanded data
 * @return a list of OrderReturn objects
 */
export async function getOrderReturns(data?: { uuid?: string; expand?: string[] }) {
  const { uuid, expand } = data || {};
  const orderId = uuid ? `${uuid}/` : '';
  const url = `${URLS.ORDERS.RETURNS}/${orderId}?${URLS.CUSTOM_ERROR}`;
  const params = {
    expand: ['customer', 'credit'].join(','),
    ...(!uuid && { limit: 9999 }),
  };

  if (Array.isArray(expand)) {
    params.expand += ',' + expand.join(',');
  }

  type UuidResponse = OrderReturnData;
  type ListResponse = { results: OrderReturnData[] };
  const response = await apiSlugSession.get<UuidResponse | ListResponse>(url, { params });

  // TODO: implement caching and read from cache
  return uuid
    ? [new OrderReturnStore(response.data as OrderReturnData)]
    : (response.data as ListResponse).results.map((r) => new OrderReturnStore(r));
}

/**
 * Submits the orderReturn
 * @param {OrderReturn & { expand?: string[] }} data The return order to be submitted and the expand list of fields to get expanded data
 */
// eslint-disable-next-line require-await
export async function submitOrderReturn(data: OrderReturn & { expand?: string[] }) {
  const { expand, ...payload } = data;

  const url = `${URLS.ORDERS.RETURNS}/?${URLS.CUSTOM_ERROR}`;
  const params = {
    expand: ['customer', 'credit'].concat(expand || []).join(','),
  };

  return apiSlugSession.post(url, payload, { params });
}

/**
 * Overwrites a whole orderReturn object (sets the returned_products too, so be sure to provide them)
 * @param {object} data Object with query params
 * @param {OrderReturn} data.orderReturn the return data (returned_products will be deleted if not provided)
 * @param {string[]} [data.expand] list of fields to get expanded data
 * @return an instance of OrderReturn
 */
export async function updateOrderReturn(data: { orderReturn: OrderReturn; expand?: string[] }) {
  const { orderReturn, expand } = data;

  const body = {
    ...orderReturn,
    // to PUT an OrderReturn, fields must be un-expanded and therefore contain only an id
    customer: orderReturn.customer.id || orderReturn.customer,
    credit: orderReturn.credit?.id || orderReturn.credit,
  };

  const expandFields = ['customer', 'credit'].concat(expand || []).join(',');
  const url = `${URLS.ORDERS.RETURNS}/${orderReturn.uuid}/?${URLS.CUSTOM_ERROR}&expand=${expandFields}`;
  const response = await apiSlugSession.put<OrderReturnData>(url, body);

  return new OrderReturnStore(response.data);
}

/**
 * Updates the notes field in an orderReturn
 * @param {object} data Object with query params
 * @param {object} data the return data to update
 * @param {string} data.uuid the id of the orderReturn being updated
 * @param {string} data.notes the new notes value
 * @return the updated data
 */
// eslint-disable-next-line require-await
export async function updateReturnNotes(data: { uuid: string; notes: string }) {
  return apiSlugSession.patch(`${URLS.ORDERS.RETURNS}/${data.uuid}/?${URLS.CUSTOM_ERROR}`, data);
}

/**
 * Transitions (the status of) an orderReturn
 * @param {string} [data.uuid] of the orderReturn
 * @param {string} [data.state] the status to which the orderReturn will be transitioned
 * @param {string[]} [data.expand] list of fields to get expanded data
 */
// eslint-disable-next-line require-await
export async function transitionOrderReturn(data: { uuid: string; state: string; expand?: string[] }) {
  const { uuid, state, expand } = data;

  const url = `${URLS.ORDERS.RETURNS}/${uuid}/${state}/?${URLS.CUSTOM_ERROR}`;
  const params = {
    expand: ['customer', 'credit'].concat(expand || []).join(','),
  };

  return apiSlugSession.post(url, undefined, { params });
}

export interface CreateSplitOrderParams {
  customerId: number;
  lineItems: Array<{
    position: number;
    productId: number;
    quantity: number;
    soldByCase: boolean;
  }>;
  notes?: string;
  parentOrderUuid: string;
}

/**
 * Creates a Split Order from a parent order
 */
export async function createSplitOrder(params: CreateSplitOrderParams) {
  const { customerId, notes, lineItems, parentOrderUuid } = params;
  const url = `${URLS.ORDERS.BASE}/${parentOrderUuid}/split/`;
  const body = {
    customerId,
    notes,
    splitLines: lineItems,
  };

  const response = await session.post<{ orderNumber: string; orderSellerNumber: string }>(url, body);

  return response.data;
}

export interface GetOrdersSplitFromParentParams {
  parentOrderUuid: string;
}

/**
 * Get all orders split from specified parent order
 * @param {string} [uuid] of the parent order
 * @return {object[]} Order[]
 */
export async function getOrdersSplitFromParent({ parentOrderUuid }: GetOrdersSplitFromParentParams) {
  const response = await session.get<OrderFromSplitSummary[]>(`${URLS.ORDERS.BASE}/${parentOrderUuid}/splits/`);

  return response.data;
}

/**
 * Get Payment Types
 */
export async function getPaymentTypes() {
  const response = await session.get(`${URLS.ORDERS.PAYMENT_TYPES}`);

  return response.data;
}

/**
 * Update order payment
 */
export async function updateOrderPayment(data: OrderPayment) {
  const response = await session.post(`${URLS.ORDERS.PAYMENTS}/?${URLS.CUSTOM_ERROR}`, data);

  return response.data;
}

/**
 * Get orders. A single order may be retrieved by providing a `uuid`
 * @param {string} params Any parameters for the endpoint.
 */
export async function getShippingOrders(params: string) {
  /**
   * These apiParams are needed to get a proper list of orders that are assignable;
   * orders that aren't assigned yet and that are not draft, etc.
   */
  const excludedStates = ['draft', 'rejected', 'cancelled', 'combined', 'complete'];
  const apiParams = ['not_assigned=true', `status__not=[${excludedStates.toString()}]`].join('&');
  const response = await session.get<PaginatedResponse<LogisticsOrder> | PaginatedResponse<LogisticsFirstOrder>>(
    `${URLS.ORDERS.SHIPPING}/?${URLS.CUSTOM_ERROR}&${apiParams}&${params}`,
  );

  return response.data;
}

/**
 * Fetches a shipping manifest for an order
 * @param {number} id of the shipping manifest
 */
// eslint-disable-next-line require-await
export async function getShippingManifest(id) {
  return session.get(`/${URLS.ORDERS.SHIPPING_MANIFESTS}/${id}/`);
}

/**
 * Generates a shipping manifest for an order
 * @param {object} [payload] Object with params to pass
 */
// eslint-disable-next-line require-await
export async function addShippingManifest(payload: object) {
  return session.post(`/${URLS.ORDERS.SHIPPING_MANIFESTS}/`, payload);
}

/**
 * Updates a shipping manifest for an order
 * @param {object} [payload] Object with params to pass
 */
// eslint-disable-next-line require-await
export async function updateShippingManifest(payload) {
  const { id, ...manifest } = payload;

  return session.put(`/${URLS.ORDERS.SHIPPING_MANIFESTS}/${id}/`, manifest);
}

export interface UpdateOrderDeliveredPayload {
  buyerPaid: boolean;
  deliveredDate: string;
  uuid: string;
}

/**
 * Mark an order as delivered.
 * @param {object} [payload] Object with params to pass
 */
// eslint-disable-next-line require-await
export async function updateOrderDelivered(payload: UpdateOrderDeliveredPayload) {
  const { uuid } = payload;

  return slugSession.post(`${uuid}/${URLS.ORDERS.MARK_DELIVERED}/?${URLS.CUSTOM_ERROR}`, payload);
}

/**
 * Send a message to Seller.
 * @param {object} [payload] Object with params to pass
 */
// eslint-disable-next-line require-await
export async function sendOrderMessage(payload) {
  return apiSlugSession.post(`${URLS.SHOP.SEND_MESSAGE}/?${URLS.CUSTOM_ERROR}`, payload);
}

/**
 * @deprecated in favor of updateOrderDetails
 */
// eslint-disable-next-line require-await
export async function updateOrderId(data) {
  const { uuid, ...payload } = data;

  return slugSession.post(`${uuid}/set_display_id/?${URLS.CUSTOM_ERROR}`, payload);
}

/**
 * commerce/orders-sent/UUID/... endpoints seem error if the company_slug is included.
 * `session` automatically appends the company_slug as a query param.
 * `createSession(URLS.INTERNAL)` does not
 */
function getSummarySession(isBuyer) {
  return isBuyer ? createSession(URLS.INTERNAL) : session;
}

/**
 * Get Order Summary
 */
export async function getOrderSummary({ uuid, isBuyer = false }: GetOrdersRequestParams) {
  const summaryPath = isBuyer ? URLS.ORDERS.BUYER_SUMMARY : URLS.ORDERS.SELLER_SUMMARY;
  const path = buildPath(summaryPath, { uuid });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;
  const summarySession = getSummarySession(isBuyer);
  const response = await summarySession.get<OrderSummaryOrder>(url);

  return response.data;
}

/**
 * Get Order Information
 */
export async function getOrderDetails({ uuid, isBuyer = false }: GetOrdersRequestParams) {
  const detailsPath = isBuyer ? URLS.ORDERS.BUYER_SUMMARY_INFO : URLS.ORDERS.SELLER_SUMMARY_INFO;
  const path = buildPath(detailsPath, { uuid });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;
  const detailsSession = getSummarySession(isBuyer);
  const response = await detailsSession.get<OrderSummaryOrderInformation>(url);

  return response.data;
}

/**
 * Update Order Information
 */
interface UpdateOrderDetailsPayload {
  buyerApproved?: OrderSummaryOrderInformation['buyerApproved'];
  paymentDueDate?: OrderSummaryOrderInformation['paymentDueDate'];
  paymentMethodId?: number;
  paymentTermId?: number;
  shipDate?: OrderSummaryOrderInformation['shipDate'];
  shippingCharge?: number;
  shippingDetails?: OrderSummaryOrderInformation['shippingDetails'];
  userDisplayId?: OrderSummaryOrderInformation['displayId'];
}

export async function updateOrderDetails({
  isBuyer,
  uuid,
  payload,
}: {
  isBuyer: boolean;
  uuid: string;
  payload: UpdateOrderDetailsPayload;
}) {
  const detailsPath = isBuyer ? URLS.ORDERS.BUYER_SUMMARY_INFO_UPDATE : URLS.ORDERS.SELLER_SUMMARY_INFO_UPDATE;
  const path = buildPath(detailsPath, { uuid });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;
  const detailsSession = getSummarySession(isBuyer);
  const response = await detailsSession.patch(url, payload);

  return response.data;
}

/**
 * Get order comments
 */
export async function getOrderComments(data: OrderSummaryComment) {
  const path = buildPath(URLS.ORDERS.SELLER_SUMMARY_COMMENTS, { uuid: data.orderNumber });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;

  return await session.get<PaginatedResponse<OrderSummaryComment>>(url);
}

/**
 * Post order comments
 */
export async function postOrderComments(data: OrderSummaryComment) {
  const path = buildPath(URLS.ORDERS.SELLER_SUMMARY_COMMENTS, { uuid: data.orderNumber });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;

  return await session.post(url, data);
}

/**
 * Get order documents
 */
export async function getOrderDocuments(uuid: string) {
  const path = buildPath(URLS.ORDERS.SELLER_SUMMARY_DOCUMENTS, { uuid });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;
  const response = await session.get<PaginatedResponse<OrderDocument>>(url);

  return response.data;
}

/**
 * Create order document
 */
export async function createOrderDocument(data: OrderDocumentPayload) {
  const { uuid, payload } = data;
  const path = buildPath(URLS.ORDERS.SELLER_SUMMARY_DOCUMENTS, { uuid });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;
  const formSession = createSession(URLS.INTERNAL, { shouldTransform: false });
  const response = await formSession.post<OrderDocument>(url, payload, {
    headers: FORM_DATA_HEADER,
  });

  return response.data;
}

/**
 * Delete Order Document
 */
export async function deleteOrderDocument(data: OrderDocumentDelete) {
  const { uuid, id: documentId } = data;
  const path = buildPath(URLS.ORDERS.SELLER_SUMMARY_DOCUMENT, { uuid, documentId });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;

  return await session.delete(url);
}

/**
 * Delete Promo Code from cart order
 */
export async function removePromoCodeFromCart(payload: OrderPromoCodeDelete) {
  const url = `${URLS.ORDERS.REMOVE_PROMO_CODE}/?${URLS.CUSTOM_ERROR}`;
  const { orderId, promoCodeId } = payload;

  return await session.delete(url, {
    data: {
      orderId,
      promoCodeId,
    },
  });
}

export async function calculateLineItems(payload) {
  const { orderId, ...data } = payload;
  const url = buildPath(URLS.ORDERS.CALCULATE_LINE_ITEMS, { orderId });

  return await session.post(url, data);
}

interface RequestParams {
  slug: string;
  uuid: string;
}
type SellThroughDetailParams = RequestParams & {
  productId: number;
};

export async function getOrderSellThroughHistory({ slug, uuid }: RequestParams): Promise<OrderSellThroughHistory> {
  const response = await session.get(buildPath(URLS.ORDERS.SELL_THROUGH_HISTORY, { uuid, slug }));
  return response.data;
}

export async function getOrderSellThroughHistoryDetail({ slug, uuid, productId }: SellThroughDetailParams) {
  const response = await session.get(buildPath(URLS.ORDERS.SELL_THROUGH_HISTORY_PRODUCT, { uuid, slug, productId }));
  return response.data;
}

export async function getOrderSellThroughHistoryAdjustments({
  slug,
  uuid,
  productId,
}: SellThroughDetailParams): Promise<OrderLineItemSellThroughHistoryAdjustmentDetail> {
  const response = await session.get(
    buildPath(URLS.ORDERS.SELL_THROUGH_HISTORY_ADJUSTMENTS, { uuid, slug, productId }),
  );
  return response.data;
}

/**
 * Get Order Receipt log
 */
export async function getOrderReceiptLog({ slug, uuid }: RequestParams) {
  const path = buildPath(URLS.ORDERS.RECEIPT_LOG, { slug, uuid });
  const url = `${path}`;
  const response = await session.get(url);

  return response.data;
}

export const uploadCsvOrderCreate = async (formData: FormData) => {
  const userStore = useUserStore();

  const session = createSession(URLS.INTERNAL + userStore.userActiveCompany?.slug, { shouldTransform: false });
  const response = await session.post(URLS.ORDERS.CSV_ORDER_CREATE, formData);

  return response.data;
};
